import { useState } from 'react';
import { upperMantendoCursor } from './inputs/UpperInput';
import { Plus } from 'lucide-react';
import { api, ApiError } from '../lib/api';

export interface CampoCadastroRapido {
  key: string;
  label: string;
  tipo?: 'text' | 'select' | 'boolean' | 'number';
  obrigatorio?: boolean;
  opcoes?: { value: string | number; label: string }[];
  valor?: string | number | boolean;
  descricao?: string;
  step?: string;
}

interface Props {
  titulo: string;
  endpoint: string;
  campos: CampoCadastroRapido[];
  onSalvo: (novo: any) => void;
  onFechar: () => void;
}

export function ModalCadastroRapido({ titulo, endpoint, campos, onSalvo, onFechar }: Props) {
  const [form, setForm] = useState<Record<string, any>>(() => {
    const inicial: Record<string, any> = {};
    campos.forEach((c) => {
      inicial[c.key] = c.valor !== undefined ? c.valor : (c.tipo === 'boolean' ? false : '');
    });
    return inicial;
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  async function handleSalvar(e: React.FormEvent) {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      const data = await api.post<any>(endpoint, form);
      onSalvo(data);
    } catch (err: any) {
      const msg = err instanceof ApiError ? err.message : 'erro ao salvar';
      setErro(/duplicad|ja existe|23505/i.test(msg) ? 'ja existe um registro com esse nome' : msg);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[1100] p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
    >
      <div className="rounded-xl shadow-xl w-full max-w-sm" style={{ backgroundColor: 'var(--panel-bg)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Plus size={16} /> {titulo}
          </h3>
          <button type="button" className="btn-fechar-modal" onClick={onFechar} aria-label="Fechar">×</button>
        </div>

        <form onSubmit={handleSalvar} className="p-5 space-y-4">
          {campos.map((campo, idx) => (
            <div key={campo.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {campo.label}
                {campo.obrigatorio && <span className="text-red-500 ml-1">*</span>}
              </label>

              {campo.tipo === 'select' ? (
                <select
                  value={form[campo.key] ?? ''}
                  onChange={(e) => setForm({ ...form, [campo.key]: e.target.value })}
                  required={campo.obrigatorio}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione...</option>
                  {(campo.opcoes || []).map((op) => (
                    <option key={String(op.value)} value={op.value}>{op.label}</option>
                  ))}
                </select>
              ) : campo.tipo === 'boolean' ? (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form[campo.key]}
                    onChange={(e) => setForm({ ...form, [campo.key]: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-600">{campo.descricao || 'Sim'}</span>
                </label>
              ) : campo.tipo === 'number' ? (
                <input
                  type="number"
                  step={campo.step || 'any'}
                  value={form[campo.key] ?? ''}
                  onChange={(e) => setForm({ ...form, [campo.key]: e.target.value })}
                  required={campo.obrigatorio}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <input
                  type="text"
                  value={form[campo.key] ?? ''}
                  onChange={(e) => setForm({ ...form, [campo.key]: upperMantendoCursor(e) })}
                  required={campo.obrigatorio}
                  autoFocus={idx === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              )}
            </div>
          ))}

          {erro && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{erro}</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onFechar}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvando}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
